import { AxiosResponse } from "axios";

import { coreService } from "../../../services";
import { Audiogram, AudiogramEditorState, AudiogramsResponse } from "../types";

export interface UpdateAudiogramPayload {
  title?: string;
  quote?: string;
  paragraph?: {
    id: string;
    text: string;
  };
  template_id?: string;
  template?: string;
  accent_color?: string;
  show_name?: string | null;
  guest_name?: string;
  waveform_style?: string;
  waveform_height?: string;
  show_waveform?: boolean;
  animated?: boolean;
  show_guest_avatar?: boolean;
  show_date?: boolean;
  background_image_key?: string | null;
  background_overlay?: number;
  background_fit?: string;
  aspect_ratio?: string;
  subtitle_styles?: {
    font?: string;
    size?: number;
    color?: string;
  };
}

function extractFontName(fontFamily: string): string {
  // Extract the first font name from a CSS font-family string like "'Inter', sans-serif"
  const match = fontFamily.match(/['"]?([^,'"]+)['"]?/);
  return match ? match[1].trim() : fontFamily;
}

function editorStateToPayload(
  state: AudiogramEditorState,
): UpdateAudiogramPayload {
  return {
    quote: state.quote,
    template_id: state.templateId || undefined,
    template: state.template,
    accent_color: state.accentColor,
    show_name: state.showName || null,
    guest_name: state.guestName,
    waveform_style: state.waveformStyle,
    waveform_height: state.waveformHeight,
    show_waveform: state.showWaveform,
    animated: state.animated,
    show_guest_avatar: state.showGuestAvatar,
    show_date: state.showDate,
    background_image_key: state.backgroundImageKey,
    background_overlay: state.backgroundOverlay,
    background_fit: state.backgroundFit,
    aspect_ratio: state.aspectRatio,
    subtitle_styles: {
      font: extractFontName(state.fontFamily),
      size: state.fontSize,
      color: state.textColor,
    },
  };
}

const getAudiograms = async (
  episodeId: string | undefined,
): Promise<AudiogramsResponse | undefined> => {
  if (!episodeId) return undefined;
  const response = await coreService.httpClient.get(
    `/audiograms/episodes/${episodeId}`,
  );
  return response.data;
};

const getAudiogramById = async (
  audiogramId: string | undefined,
): Promise<Audiogram | undefined> => {
  if (!audiogramId) return undefined;
  const response = await coreService.httpClient.get(
    `/audiograms/${audiogramId}`,
  );
  const { data } = response;
  return data?.audiogram ?? data;
};

const updateAudiogram = async (
  audiogramId: string,
  state: AudiogramEditorState,
  paragraph?: { id: string; text: string },
): Promise<AxiosResponse<void>> => {
  return coreService.httpClient.patch(`/audiograms/${audiogramId}`, {
    ...editorStateToPayload(state),
    ...(paragraph ? { paragraph } : {}),
  });
};

export type CustomAudiogramSuggestion = {
  start: number;
  end: number;
  title: string;
};

const generateCustomAudiogramIdeas = async (
  episodeId: string,
  prompt: string,
  allowAdditionalCharges?: boolean,
): Promise<CustomAudiogramSuggestion[]> => {
  const response = await coreService.httpClient.post(
    `audiograms/episodes/${episodeId}/custom`,
    {
      prompt,
      allow_additional_charges: allowAdditionalCharges || undefined,
    },
  );
  return response.data;
};

const saveCustomAudiogram = async (
  episodeId: string,
  suggestion: CustomAudiogramSuggestion,
): Promise<void> => {
  await coreService.httpClient.post(
    `audiograms/episodes/${episodeId}/custom/generate`,
    suggestion,
  );
};

const getBackgroundUploadUrl = async (
  audiogramId: string,
  contentType: string,
  fileSize: number,
): Promise<{ upload_url: string; key: string }> => {
  const response = await coreService.httpClient.post(
    `/audiograms/${audiogramId}/background-upload-url`,
    { content_type: contentType, file_size: fileSize },
  );
  return response.data;
};

const uploadBackgroundImage = async (
  audiogramId: string,
  file: File,
): Promise<string> => {
  const { upload_url: uploadUrl, key } = await getBackgroundUploadUrl(
    audiogramId,
    file.type,
    file.size,
  );
  await fetch(uploadUrl, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": file.type },
  });
  return key;
};

export interface ExportAudiogramPayload {
  video_resolution?: string;
  compression_level?: string;
  fps?: number;
  include_subtitle: boolean;
  format?: string;
}

const exportAudiogram = async (
  audiogramId: string,
  payload: ExportAudiogramPayload,
): Promise<void> => {
  await coreService.httpClient.post(
    `/audiograms/${audiogramId}/export`,
    payload,
  );
};

export const audiogramsService = {
  getAudiograms,
  getAudiogramById,
  updateAudiogram,
  editorStateToPayload,
  generateCustomAudiogramIdeas,
  saveCustomAudiogram,
  uploadBackgroundImage,
  exportAudiogram,
};
